<template>
  <SimplePage title="受信メール" hideStoreName showBack :onHome="back">
    <template #main>
      <div class="desc">
        <p>他社予約取り込み用のメールアドレス宛に届いたメールの内容を確認できます。</p>
        <p class="exp">
          ※保管されるメールは、過去90日分までとなります。<br />
          ※メールの取り込みに時間がかかる場合がございます。メールが届かない場合は、各メールの転送設定が正しく設定されているかご確認ください。
        </p>
      </div>
      <div class="filter">
        <SelectBox v-model.number="media" :items="mediaMap" />
        <SelectBox v-model.number="status" :items="statusMap" />
        <CalendarInput
          v-model="from"
          clearable
          :holidayList="allHolidays"
          :businessDay="businessDay"
          :reserveCalendar="reserveCalendar"
          :busy="carendarBusy"
          @setDate="setDate"
          @beforeShow="setDate(from)"
        />
        <span>〜</span>
        <CalendarInput
          v-model="to"
          clearable
          :holidayList="allHolidays"
          :businessDay="businessDay"
          :reserveCalendar="reserveCalendar"
          :busy="carendarBusy"
          @setDate="setDate"
          @beforeShow="setDate(to)"
        />
        <Button small auto primary @click="search" :disabled="busy">表示</Button>
      </div>
      <DataTable
        class="mails"
        :data="allMails"
        :minItemHeight="64"
        :columns="[
          { key: 'media', name: 'メディア', format: media => mediaMap.get(media) },
          { key: 'status', name: 'ステータス' },
          { key: 'subject', name: '件名' },
          { key: 'importStatus', name: '予約', format: status => importStatusMap.get(status) },
          { key: 'receptionDate', name: '受付日時', format: date => formatDate(date, 'MM/DD HH:mm') },
        ]"
        nodata="該当するメールはありません"
        :loading="busy"
        :scrollOver="{ handler: fetch, distance: 800 }"
        selectable
        :loadInitial="loadInitial"
        @select="showMessage"
      >
        <template #status="{ status }">
          <ReceptionStatus :status="status" />
        </template>
        <template #subject="{subject}">
          <span>{{ subject }}</span>
        </template>
        <template #nodata>
          <MailIcon class="nodata-icon" />
          <p class="nodata-text">該当するメールはありません。</p>
        </template>
      </DataTable>
      <Modal v-if="detail" title="メール内容" size="big" :loading="busy" @close="closeMessage">
        <template #content>
          <div class="detail">
            <div class="header">
              <div class="info">
                <ReceptionStatus :status="detail.status" />
                <span class="time">{{ formatDate(detail.receptionDate, 'YYYY/M/D (dd) HH：mm') }}</span>
              </div>
              <h3 class="subject">{{ detail.subject }}</h3>
              <span class="from">送信元：{{ detail.from }}</span>
            </div>
            <p class="message" v-if="html" v-html="html" />
          </div>
        </template>
        <template #footer-center>
          <Button @click="closeMessage">閉じる</Button>
        </template>
      </Modal>
    </template>
  </SimplePage>
</template>

<script>
import { parse } from 'date-fns'
import { escape, isEqual, pick } from 'lodash'
import { mapGetters, mapState } from 'vuex'
import { formatDate, today } from '@/utils/date'
import { isWebView } from '@/utils/device'
import SimplePage from '@/components/templates/SimplePage'
import CalendarInput from '@/components/molecules/CalendarInput'
import DataTable from '@/components/molecules/DataTable'
import Button from '@/components/atoms/Button'
import ReceptionStatus from '@/components/atoms/ReceptionStatus'
import SelectBox from '@/components/atoms/SelectBox'
import Modal from '@/components/molecules/Modal'
import MailIcon from '@/components/icons/mail'
import urlRegex from 'url-regex'

export default {
  components: {
    SimplePage,
    CalendarInput,
    DataTable,
    Button,
    Modal,
    ReceptionStatus,
    SelectBox,
    MailIcon,
  },
  data() {
    const { media, status, from, to } = this.$route.query
    return {
      media: media || 0,
      status: status || 0,
      from: from && from,
      to: to && to,
      detail: null,
    }
  },
  computed: {
    ...mapState({
      busy: state => state.mails.busy,
      businessDay: state => state.businessDay.list,
      carendarBusy: state => state.init.busy,
      reserveCalendar: state => state.init.reserveCalendar,
    }),
    ...mapGetters({
      allHolidays: 'holidays/all',
      allMails: 'mails/all',
    }),
    mediaMap: () =>
      new Map([
        [0, 'すべて'],
        [1, 'ホットペッパーグルメ'],
        [2, '食べログ'],
        [3, '一休'],
        [4, 'OZmall'],
        [5, 'PayPayグルメ'],
        [99, 'その他'],
      ]),
    statusMap: () =>
      new Map([
        [0, '全ステータス'],
        [1, '新規受付'],
        [2, '変更'],
        [3, 'キャンセル'],
        [99, 'その他'],
      ]),
    importStatusMap: () =>
      new Map([
        [0, '--'],
        [1, '取込済'],
        [2, '重複'],
      ]),
    query() {
      const { media, status, from, to } = this.$route.query
      return {
        media: (media && +media) || 0,
        status: (status && +status) || 0,
        from: from && formatDate(parse(from)),
        to: to && formatDate(parse(to)),
      }
    },
    html() {
      return escape(this.detail.body || '').replace(urlRegex(), href => {
        const url = new URL(href)
        if (isWebView()) url.searchParams.append('browser', 'safari')
        return `<a href="${url.href}" target="_blank">${href}</a>`
      })
    },
    loadInitial() {
      return this.busy && !this.allMails.length
    },
  },
  watch: {
    $route() {
      this.setQuery()
      this.clear()
      this.fetch()
    },
    from(from) {
      if (from && (!this.to || this.to < from)) this.to = from
    },
    to(to) {
      if (to && (!this.from || to < this.from)) this.from = to
    },
  },
  async created() {
    await this.$store.dispatch('holidays/get')
    await this.$store.dispatch('businessDay/get')
    this.clear()
    this.fetch()
  },
  methods: {
    formatDate,
    setQuery() {
      this.media = this.query.media
      this.status = this.query.status
      this.from = this.query.from
      this.to = this.query.to
    },
    clear() {
      this.$store.commit('mails/clear')
    },
    fetch() {
      this.$store.dispatch('mails/get', this.query)
    },
    search() {
      const query = pick(this, ['media', 'status', 'from', 'to'])
      if (isEqual(this.query, query)) {
        this.clear()
        this.fetch()
      } else {
        this.$router.push({ query })
      }
    },
    back() {
      location.assign('/mailconversion/init')
    },
    showMessage(item) {
      this.detail = item
    },
    closeMessage() {
      this.detail = null
    },
    async setDate(date) {
      await this.$store.dispatch('init/createCalendar', { date: date || today() })
    },
  },
}
</script>

<style lang="postcss" scoped>
.desc {
  display: grid;
  grid-gap: 8px;
  padding: 24px 24px 16px;
}

.exp {
  font-size: 12px;
  line-height: 16px;
}

.filter {
  align-items: center;
  background: var(--very-light-gray-2);
  box-sizing: border-box;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: repeat(2, 1fr) repeat(3, auto) 88px;
  height: 56px;
  padding: 8px;
}

.mails {
  >>> td {
    padding: 8px 16px;
  }

  >>> .media {
    min-width: 188px;
    width: 188px;
  }

  >>> .status {
    min-width: 136px;
    width: 136px;
  }

  >>> .importStatus {
    justify-content: center;
    min-width: 80px;
    width: 80px;
  }

  >>> .receptionDate {
    min-width: 136px;
    width: 136px;
  }
}

.detail {
  padding: 24px 32px;
}

.header {
  border-bottom: 1px solid var(--very-light-gray-2);
  display: grid;
  grid-gap: 8px;
  padding: 0 8px 16px;
}

.info {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.time {
  font-size: 13px;
}

.subject {
  font-size: 15px;
  font-weight: 600;
}

.from {
  font-size: 13px;
}

.message {
  font-size: 13px;
  line-height: 20px;
  padding: 16px 8px 0;
  white-space: pre-wrap;
}

.nodata-icon {
  fill: var(--light-gray);
  height: 60px;
  width: 60px;
}

.nodata-text {
  margin-top: 10px;
}

>>> td.subject span {
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
</style>
